import React, { useMemo, useState } from 'react';

// Редактор интервалов тарифа доставки: строки «до X км — Y ₽», генератор сетки
// («до 200 км шагом 5 км, +150 ₽») и живая проверка цены. Управляемый компонент:
// строки (возможно, недозаполненные во время ввода) живут у родителя, перед
// сохранением родитель вызывает validateTariffRows.

export function validateTariffRows(rows) {
  if (!rows.length) return { ok: false, error: 'Добавьте хотя бы один интервал' };
  if (rows.length > 500) return { ok: false, error: 'Слишком много интервалов (максимум 500)' };
  const intervals = [];
  let prev = 0;
  for (let i = 0; i < rows.length; i++) {
    const km = parseFloat(rows[i].up_to_km);
    const price = parseFloat(rows[i].price);
    if (!Number.isFinite(km) || km <= 0) {
      return { ok: false, error: `Строка ${i + 1}: заполните границу в км`, row: i };
    }
    if (!Number.isFinite(price) || price < 0) {
      return { ok: false, error: `Строка ${i + 1}: заполните цену`, row: i };
    }
    if (km <= prev) {
      return { ok: false, error: `Строка ${i + 1}: ${km} км не больше предыдущей границы (${prev} км)`, row: i };
    }
    if (km > 1000) {
      return { ok: false, error: `Строка ${i + 1}: больше 1000 км не поддерживается`, row: i };
    }
    intervals.push({ up_to_km: km, price });
    prev = km;
  }
  return { ok: true, intervals };
}

const fmt = (v) => (Number.isFinite(v) ? String(+(+v).toFixed(2)) : '');
const digits = (s) => String(s).replace(/[^0-9.]/g, '');

// Сетка по умолчанию — прежние 8 ступеней по 5 км
const DEFAULT_ROWS = [250, 350, 450, 550, 650, 750, 850, 950]
  .map((price, i) => ({ up_to_km: (i + 1) * 5, price }));

function DeliveryTariffEditor({ rows, onChange }) {
  const [genOpen, setGenOpen] = useState(false);
  const [gen, setGen] = useState({ to: '', step: '5', inc: '150', firstPrice: '' });
  const [testKm, setTestKm] = useState('');

  const validation = useMemo(() => validateTariffRows(rows), [rows]);

  const setRow = (i, field, value) => {
    const next = rows.slice();
    next[i] = { ...next[i], [field]: value };
    onChange(next);
  };
  const removeRow = (i) => onChange(rows.filter((_, idx) => idx !== i));
  // Новая строка добавляется ПУСТОЙ — границу и цену вписываешь сам (хоть 3 км);
  // подсказка-продолжение показывается плейсхолдером.
  const addRow = () => onChange([...rows, { up_to_km: '', price: '' }]);
  const suggestFor = (i) => {
    const last = rows[i - 1];
    const prev = rows[i - 2];
    const lastKm = last ? parseFloat(last.up_to_km) : 0;
    const stepKm = last && prev ? parseFloat(last.up_to_km) - parseFloat(prev.up_to_km) : 5;
    const lastPrice = last ? parseFloat(last.price) : NaN;
    const stepPrice = last && prev ? parseFloat(last.price) - parseFloat(prev.price) : 150;
    return {
      km: Number.isFinite(lastKm) && Number.isFinite(stepKm) ? fmt(lastKm + stepKm) : 'км',
      price: Number.isFinite(lastPrice) && Number.isFinite(stepPrice) ? fmt(lastPrice + stepPrice) : 'цена',
    };
  };

  // Генератор: продолжает список от последней границы (или с нуля) арифметической сеткой
  const genPlan = useMemo(() => {
    const to = parseFloat(gen.to);
    const step = parseFloat(gen.step);
    const inc = parseFloat(gen.inc);
    const last = rows[rows.length - 1];
    const fromKm = last ? parseFloat(last.up_to_km) : 0;
    const basePrice = last ? parseFloat(last.price) : parseFloat(gen.firstPrice) - (Number.isFinite(inc) ? inc : 0);
    if (!Number.isFinite(to) || !Number.isFinite(step) || step <= 0 || !Number.isFinite(inc)) return null;
    if (!Number.isFinite(fromKm) || !Number.isFinite(basePrice)) return null;
    if (to <= fromKm) return null;
    const count = Math.ceil((to - fromKm) / step);
    if (count < 1 || count + rows.length > 500) return null;
    const newRows = [];
    for (let i = 1; i <= count; i++) {
      newRows.push({
        up_to_km: fmt(Math.min(fromKm + i * step, to)),
        price: fmt(basePrice + i * inc),
      });
    }
    return { count, newRows };
  }, [gen, rows]);

  const applyGen = () => {
    if (!genPlan) return;
    onChange([...rows, ...genPlan.newRows]);
    setGenOpen(false);
  };

  // Живая проверка по текущим (несохранённым) значениям
  const testResult = useMemo(() => {
    const km = parseFloat(testKm);
    if (!Number.isFinite(km) || km < 0 || !validation.ok) return null;
    let prev = 0;
    for (const it of validation.intervals) {
      if (km <= it.up_to_km) return { price: it.price, zone: `${prev}–${it.up_to_km} км` };
      prev = it.up_to_km;
    }
    return { negotiation: true, beyond: prev };
  }, [testKm, validation]);

  const lastBound = validation.ok
    ? validation.intervals[validation.intervals.length - 1].up_to_km
    : null;

  return (
    <div>
      {rows.length === 0 ? (
        <div className="tariff-empty">
          Интервалы не заданы.
          <button className="btn btn-secondary btn-sm" onClick={() => onChange(DEFAULT_ROWS.map(r => ({ up_to_km: fmt(r.up_to_km), price: fmt(r.price) })))}>
            Заполнить по умолчанию
          </button>
        </div>
      ) : (
        <div className="tariff-rows">
          {rows.map((r, i) => {
            const prevKm = i === 0 ? 0 : parseFloat(rows[i - 1].up_to_km);
            const hasError = !validation.ok && validation.row === i;
            return (
              <div className={`tariff-row${hasError ? ' tariff-row-error' : ''}`} key={i}>
                <span className="tariff-bound">{Number.isFinite(prevKm) ? `${+prevKm.toFixed(2)}` : '?'} –</span>
                <input
                  type="text" inputMode="decimal" className="form-input tariff-input"
                  value={r.up_to_km}
                  onChange={(e) => setRow(i, 'up_to_km', digits(e.target.value))}
                  placeholder={suggestFor(i).km}
                />
                <span className="tariff-unit">км</span>
                <input
                  type="text" inputMode="decimal" className="form-input tariff-input"
                  value={r.price}
                  onChange={(e) => setRow(i, 'price', digits(e.target.value))}
                  placeholder={suggestFor(i).price}
                />
                <span className="tariff-unit">₽</span>
                <button className="tariff-remove" title="Удалить интервал" onClick={() => removeRow(i)}>×</button>
              </div>
            );
          })}
        </div>
      )}

      <div className="tariff-actions">
        <button className="btn btn-secondary btn-sm" onClick={addRow}>+ Добавить интервал</button>
        <button className="btn btn-secondary btn-sm" onClick={() => setGenOpen(!genOpen)}>
          ⚙ Сгенерировать сетку
        </button>
      </div>

      {genOpen && (
        <div className="tariff-gen">
          <div className="tariff-gen-fields">
            {rows.length === 0 && (
              <label>первая цена
                <input type="text" inputMode="decimal" className="form-input tariff-input"
                  value={gen.firstPrice} onChange={(e) => setGen({ ...gen, firstPrice: digits(e.target.value) })} placeholder="250" />₽
              </label>
            )}
            <label>до
              <input type="text" inputMode="decimal" className="form-input tariff-input"
                value={gen.to} onChange={(e) => setGen({ ...gen, to: digits(e.target.value) })} placeholder="200" />км
            </label>
            <label>шаг
              <input type="text" inputMode="decimal" className="form-input tariff-input"
                value={gen.step} onChange={(e) => setGen({ ...gen, step: digits(e.target.value) })} />км
            </label>
            <label>прибавка
              <input type="text" inputMode="decimal" className="form-input tariff-input"
                value={gen.inc} onChange={(e) => setGen({ ...gen, inc: digits(e.target.value) })} />₽
            </label>
          </div>
          <div className="tariff-gen-footer">
            <span className="tariff-hint">
              {genPlan
                ? `создаст ${genPlan.count} интервал(ов)${rows.length ? ` после ${rows[rows.length - 1].up_to_km} км` : ''}`
                : 'заполните поля: граница должна быть дальше последнего интервала'}
            </span>
            <button className="btn btn-primary btn-sm" disabled={!genPlan} onClick={applyGen}>Сгенерировать</button>
          </div>
        </div>
      )}

      <div className="tariff-test">
        <span>Проверка цены — введите расстояние:</span>
        <input
          type="text" inputMode="decimal" className="form-input tariff-input"
          value={testKm} onChange={(e) => setTestKm(digits(e.target.value))} placeholder="47"
        />
        <span>км</span>
        {testKm !== '' && (
          <strong>
            {testResult == null
              ? 'исправьте интервалы выше'
              : testResult.negotiation
                ? '— по согласованию'
                : `— ${testResult.price} ₽ (зона ${testResult.zone})`}
          </strong>
        )}
      </div>

      {lastBound != null && (
        <div className="tariff-hint">свыше {lastBound} км — доставка по индивидуальному согласованию</div>
      )}
    </div>
  );
}

export default DeliveryTariffEditor;
